import { ButtonLink } from '@n26/nxd'
import { useContext, useCallback } from 'react'
import { useLocation } from '@remix-run/react'
import { getOptimizelyTrackingEventKey } from '@helpers/getOptimizelyTrackingEventKey'
import { useLocaliseString } from '@hooks/useLocaliseString'
import { useGetCountryLanguage } from '@hooks/useGetCountryLanguage'
import { arrayIncludes } from '@helpers/arrayIncludes'
import { WAIT_LIST_COUNTRIES } from '@constants/countryDetails'
import { trackGTM } from '@helpers/core/trackGTM'
import { WebsiteModuleContext } from '@contexts/WebsiteModuleContext'
import { useEventTracker } from '@hooks/useEventTracker'
import type { LandingPageData, ModifiedQRCodeCTA } from '@pages/LandingPage'
import { useGetLocale } from '@hooks/useGetLocale'
import { convertToQueryString } from '@helpers/core/queryString'
import { getCountryLanguageFromLocale } from '@helpers/getCountryLanguageFromLocale'
import { getISOCountryFromLocale } from '@helpers/getISOCountryFromLocale'
import { QrCodeCta } from '@components/QrCodeCTA'
import { useQueryStringPreservedUrl } from '@hooks/useQueryStringPreservedUrl'
import { useOptimizelyEventTracker } from '@hooks/useOptimizelyEventTracker'
import { SIGNUP_INITIATED_EVENT } from '@constants/optimizely'

// @TODO: Improve typing when extending SignupButton component
// to other pages than Landing Pages
type LandingPagePageLink = Extract<
  NonNullable<LandingPageData['landingPageData']['ctaLink']>['pageLink'],
  { __typename?: 'Page' }
>

export type SignupButtonProps = {
  pageCtaLink?: LandingPageData['landingPageData']['ctaLink']
  qrCodeCta?: ModifiedQRCodeCTA
  registrationUrl: string
  variant?: 'primary' | 'secondary' | 'tertiary'
  size?: 'standard' | 'small'
  className?: string
  trackingName?: string | null
  productPreference?: string | null
  labelOverride?: string
  useWithoutQueryParams?: boolean
  color?: 'neutral' | 'petrol' | 'teal' | 'wheat' | 'rhubarb'
}

function getTrackingEvent({
  isSUI,
  isQrCodeCta,
  qrCodeCta,
  ctaText,
  ctaOptimizelyEvent,
  productPreference,
  destinationUrl,
}: {
  isSUI: boolean
  isQrCodeCta: boolean
  qrCodeCta?: ModifiedQRCodeCTA
  ctaText?: string
  ctaOptimizelyEvent?: string | null
  productPreference?: string | null
  destinationUrl?: string
}) {
  if (isQrCodeCta) {
    return {
      GTMEvent: 'cta_click',
      action: 'website.web_to_app.sui_button_clicked',
      ctaText: qrCodeCta?.ctaLabelApp,
      destinationUrl: qrCodeCta?.appDownloadData?.appDownloadUrl,
    }
  }

  if (isSUI) {
    return {
      GTMEvent: 'website_signup_initiated_click',
      action: 'website.sui.clicked',
      optimizelyEventKey: getOptimizelyTrackingEventKey({
        ctaOptimizelyEvent,
        productPreference,
        defaultOptimizelyEvent: SIGNUP_INITIATED_EVENT,
      }),
      ctaText,
      destinationUrl,
    }
  }

  return {
    GTMEvent: 'cta_click',
    action: 'website.other_cta.clicked',
    optimizelyEventKey: getOptimizelyTrackingEventKey({
      ctaOptimizelyEvent,
      productPreference,
    }),
    ctaText,
    destinationUrl,
  }
}

export const SignupButton = ({
  pageCtaLink,
  qrCodeCta,
  registrationUrl,
  variant = 'primary',
  size = 'standard',
  className = '',
  trackingName: ch,
  productPreference: pr,
  labelOverride,
  useWithoutQueryParams,
  color = 'teal',
}: SignupButtonProps) => {
  const locale = useGetLocale()
  const { country, language } = useGetCountryLanguage()
  const { pathname } = useLocation()
  const localiseString = useLocaliseString()
  const { moduleId, moduleType } = useContext(WebsiteModuleContext) || {
    moduleId: null,
    moduleType: null,
  }

  const trackOptimizelyEvent = useOptimizelyEventTracker()

  const { language: lang } = getCountryLanguageFromLocale(locale)
  const { iso3CountryCode: residencyCountryCode } =
    getISOCountryFromLocale(locale)

  const queryString = convertToQueryString(
    {
      pr,
      ch,
      residencyCountryCode,
      lang,
    },
    { addQueryPrefix: true }
  )

  const getCtaLabel = useCallback(() => {
    if (labelOverride) return labelOverride

    const { text } = pageCtaLink || {}
    const hasWaitingList = arrayIncludes(WAIT_LIST_COUNTRIES, country)

    if (hasWaitingList) {
      return localiseString('signupWaitingListButtonLabel')
    }

    return text || localiseString('signupButtonLabel')
  }, [localiseString, pageCtaLink, country, labelOverride])

  const ctaLabel = getCtaLabel()

  const getCtaHref = () => {
    if (useWithoutQueryParams) return registrationUrl

    if (pageCtaLink) {
      const externalLink = pageCtaLink.link

      if (externalLink) {
        return externalLink + queryString
      }

      // @TODO: Remove casting when better typing solution is in place
      const slug = (pageCtaLink.pageLink as LandingPagePageLink)?.slug

      if (slug) {
        return `/${locale}/${slug}${queryString}`
      }
    }

    return registrationUrl + queryString
  }

  const href = getCtaHref()
  const hrefWithPreservedQueryStrings = useQueryStringPreservedUrl(href)

  // The signup button's URL can be customised through contentful hence, it could have a non SUI URL
  const isSUI = hrefWithPreservedQueryStrings.includes(registrationUrl)
  const isQrCodeCta = Boolean(
    qrCodeCta && qrCodeCta.heading && qrCodeCta.instructions
  )

  const { track } = useEventTracker()

  const handleClick = ({
    currentTarget,
  }:
    | React.MouseEvent<HTMLAnchorElement>
    | React.MouseEvent<HTMLButtonElement>) => {
    const { GTMEvent, action, ctaText, optimizelyEventKey, destinationUrl } =
      getTrackingEvent({
        isSUI,
        isQrCodeCta,
        qrCodeCta,
        ctaOptimizelyEvent: pageCtaLink?.optimizelyEventKey,
        productPreference: pr,
        ctaText: ctaLabel,
        destinationUrl: hrefWithPreservedQueryStrings,
      })

    trackGTM({
      event: GTMEvent,
      // eslint-disable-next-line camelcase
      cta_text: ctaText,
      // eslint-disable-next-line camelcase
      destination_url: destinationUrl,
      // eslint-disable-next-line camelcase
      module_id: moduleId,
      // eslint-disable-next-line camelcase
      module_type: moduleType,
    })

    const queriedCTAs = document.querySelectorAll('[data-cta="true"]')
    const CTAs = [...queriedCTAs]
    const ctaTotal = CTAs.length
    const ctaNum = CTAs.findIndex(cta => currentTarget === cta) + 1

    track({
      action,
      label: {
        locale: language,
        country,
        // eslint-disable-next-line camelcase
        cta_total: ctaTotal,
        // eslint-disable-next-line camelcase
        cta_num: ctaNum,
        // eslint-disable-next-line camelcase
        destination_url: destinationUrl,
        // eslint-disable-next-line camelcase
        origin_path: pathname,
      },
    })

    if (optimizelyEventKey) {
      trackOptimizelyEvent({ eventKey: optimizelyEventKey })
    }
  }

  return (
    <div className={`${className} min-w-fit`}>
      {isQrCodeCta ? (
        <QrCodeCta
          qrCodeCta={qrCodeCta}
          pageHref={hrefWithPreservedQueryStrings}
          size={size}
          variant={variant}
          onClick={handleClick}
        />
      ) : (
        <ButtonLink
          onClick={handleClick}
          href={hrefWithPreservedQueryStrings}
          size={size}
          variant={variant}
          color={color}
        >
          {ctaLabel}
        </ButtonLink>
      )}
    </div>
  )
}
